/**
 * <StudentCloseAccountPage> — « Fermer mon compte » (page).
 *
 * Les textes de l'écran d'aujourd'hui, dans son ordre : l'avis d'un abonnement encore valable
 * (seulement quand il y en a un), ce que fermer veut dire et le bouton, puis « Rouvrir mon
 * compte ». La confirmation suit la convention du design system : « Supprimer mon compte » est
 * le bouton rouge, « Annuler » la sortie (l'ancien dialogue les posait à l'envers).
 *
 * Pendant la suppression le bouton tourne, ce que l'ancien écran ne montrait pas. Un échec se dit
 * en `<Snackbar>`.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Paragraph } from "../../atoms/typography/Paragraph"
import { AlertCard } from "../../molecules/AlertCard"
import { Card } from "../../molecules/Card"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { SettingsSection } from "../../molecules/SettingsSection"
import { Snackbar } from "../../molecules/Snackbar"
import { AccountSettingsTemplate } from "../../templates/AccountSettingsTemplate"

export type StudentCloseAccountPageProps = {
    onBack: () => void
    /**
     * Le nom de l'abonnement encore valable, tel que l'écran d'aujourd'hui l'écrit : « PREMIUM »,
     * « PLUS ». Absent : pas d'avis.
     */
    activeOffer?: string | null
    /**
     * Le compte ne peut pas se fermer d'ici, et pourquoi — « …tu es actuellement tuteur et tu as
     * encore des élèves ». Présent : la phrase remplace le bouton et « Rouvrir mon compte ». Défaut
     * absent : la page d'avant, telle quelle.
     */
    closeBlockedReason?: string | null
    /** « Fermer mon compte » : ouvre la confirmation. */
    onAskClose: () => void
    confirmVisible?: boolean
    onConfirmClose: () => void
    onCancelClose: () => void
    /** Le voile ou le retour d'Android sur la confirmation. Absent : `onCancelClose`. */
    onDismissClose?: () => void
    /** La suppression est en cours : le bouton tourne et ne répond plus. */
    closing?: boolean
    /** La suppression n'est pas passée : le message du serveur. */
    closeError?: string | null
    onDismissCloseError?: () => void
    testID?: string
}

const noop = () => {}

export function StudentCloseAccountPage(props: StudentCloseAccountPageProps) {
    const {
        onBack,
        activeOffer,
        closeBlockedReason,
        onAskClose,
        confirmVisible = false,
        onConfirmClose,
        onCancelClose,
        onDismissClose,
        closing = false,
        closeError,
        onDismissCloseError,
        testID,
    } = props

    return (
        <AccountSettingsTemplate testID={testID} title="Fermer mon compte" onBack={onBack}>
            <Stack gap="4">
                {activeOffer != null && activeOffer !== "" && (
                    <AlertCard
                        severity="warning"
                        title={`Ton abonnement Masteur ${activeOffer} est encore valable.`}
                        message="Si tu souhaites annuler ton abonnement, tu peux passer à Masteur BASIC pour arrêter de payer : lorsque ton abonnement aura expiré, tu pourras toujours accéder à l'univers Masteur, mais sans analyse de la qualité de ton travail et sans tuteur."
                    />
                )}

                {closeBlockedReason != null && closeBlockedReason !== "" ? (
                    <AlertCard severity="info" message={closeBlockedReason} />
                ) : (
                    <>
                        <Card p="4">
                            <Stack gap="4">
                                <Paragraph>
                                    Pour supprimer définitivement ton compte de toutes les applications et de tous les
                                    services Masteur, tu peux fermer ton compte toi-même :
                                </Paragraph>
                                <Button
                                    label="Fermer mon compte"
                                    variant="outline"
                                    color="danger"
                                    fullWidth
                                    loading={closing}
                                    onPress={onAskClose}
                                />
                            </Stack>
                        </Card>

                        <SettingsSection title="Rouvrir mon compte">
                            <Stack gap="2">
                                <Paragraph>
                                    Après avoir fermé ton compte, il sera supprimé définitivement dans un délai de 7
                                    jours.
                                </Paragraph>
                                <Paragraph>
                                    Passé ce délai, tu ne pourras plus le réactiver (mais tu pourras toujours en créer
                                    un autre).
                                </Paragraph>
                            </Stack>
                        </SettingsSection>
                    </>
                )}
            </Stack>

            <ConfirmDialog
                visible={confirmVisible}
                title="Veux-tu vraiment fermer ton compte ?"
                message="Toutes tes données seront supprimées dans un délai de 7 jours."
                confirmLabel="Supprimer mon compte"
                cancelLabel="Annuler"
                onConfirm={onConfirmClose}
                onCancel={onCancelClose}
                onDismiss={onDismissClose ?? onCancelClose}
            />

            <Snackbar
                visible={closeError != null}
                message={closeError ?? ""}
                tone="danger"
                onDismiss={onDismissCloseError ?? noop}
            />
        </AccountSettingsTemplate>
    )
}
